import { useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import type { FilterFacets } from '@/shared/types'
import { useAppStore } from '@/lib/store'
import { localizedName } from '@/lib/format'

type ChipState = 'neutral' | 'include' | 'exclude'

const SEARCHABLE_THRESHOLD = 8
const COLLAPSE_LIMIT = 10

function chipState(
  include: Record<string, string[]>,
  exclude: Record<string, string[]>,
  specName: string,
  option: string,
): ChipState {
  if (include[specName]?.includes(option)) return 'include'
  if (exclude[specName]?.includes(option)) return 'exclude'
  return 'neutral'
}

interface FiltersT {
  title: string
  legend: string
  selected: string
  searchPlaceholder: string
  noMatches: string
  showMore: string
  showLess: string
  priceRange: string
  priceRangeWithCurrency?: (currency: string) => string
  priceMin: string
  priceMax: string
  variant: string
  variantAny: string
  clear: string
  apply: string
}

interface Props {
  facets: FilterFacets
  filterLabel: string
  T: FiltersT
  include: Record<string, string[]>
  exclude: Record<string, string[]>
  onCycle: (specName: string, option: string) => void
  onRemove: (specName: string, option: string) => void
  priceMin: string
  priceMax: string
  onPriceMinChange: (value: string) => void
  onPriceMaxChange: (value: string) => void
  variantName: string
  onVariantNameChange: (value: string) => void
  activeCount: number
  onClear: () => void
  isDarkTheme?: boolean
  currencyLabel?: string
}

export function CatalogFilters({
  facets,
  filterLabel,
  T,
  include,
  exclude,
  onCycle,
  onRemove,
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
  variantName,
  onVariantNameChange,
  activeCount,
  onClear,
  isDarkTheme = false,
  currencyLabel = 'UAH',
}: Props) {
  const lang = useAppStore((s) => s.lang)
  const [isOpen, setIsOpen] = useState(false)
  const [specSearch, setSpecSearch] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const specMap = new Map(facets.specs.map((s) => [s.code || s.name_uk, s]))
  const priceRangeLabel = T.priceRangeWithCurrency
    ? T.priceRangeWithCurrency(currencyLabel)
    : T.priceRange

  const getLocalizedSpecName = (codeOrName: string) => {
    const spec = specMap.get(codeOrName)
    if (!spec) return codeOrName
    return localizedName(lang, spec.name_uk, spec.name_en)
  }

  const selected = [
    ...Object.entries(include).flatMap(([specName, options]) =>
      options.map((option) => ({ specName, option, kind: 'include' as const })),
    ),
    ...Object.entries(exclude).flatMap(([specName, options]) =>
      options.map((option) => ({ specName, option, kind: 'exclude' as const })),
    ),
  ]

  const chipClasses: Record<ChipState, string> = {
    neutral: isDarkTheme
      ? 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-[#c9a24b]'
      : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300',
    include: 'border-emerald-600 bg-emerald-600 text-white font-semibold',
    exclude: isDarkTheme
      ? 'border-rose-500/40 bg-rose-500/20 text-rose-300 line-through'
      : 'border-red-300 bg-red-50 text-red-500 line-through',
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`relative flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium shadow-sm transition md:px-4 md:py-2 md:text-sm ${
          activeCount > 0
            ? isDarkTheme
              ? 'border-[#c9a24b] bg-[#c9a24b] text-[#17120f] font-semibold shadow-md shadow-[#c9a24b]/10'
              : 'border-brand-600 bg-brand-600 text-white shadow-brand-600/20'
            : isDarkTheme
              ? 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-[#c9a24b]'
              : 'border-gray-300 bg-white text-gray-700 hover:border-brand-300 hover:text-brand-700'
        }`}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 md:h-4 md:w-4" />
        {filterLabel}
        {activeCount > 0 && (
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
            isDarkTheme ? 'bg-[#17120f] text-[#c9a24b]' : 'bg-white/90 text-brand-600'
          }`}>
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/60 backdrop-blur-xs md:items-center md:justify-center md:p-6"
          onClick={() => setIsOpen(false)}
        >
          <div
            className={`max-h-[88vh] w-full overflow-y-auto rounded-t-2xl p-4 shadow-2xl md:max-h-[85vh] md:w-[min(880px,calc(100vw-48px))] md:rounded-[26px] md:border md:p-0 ${
              isDarkTheme
                ? 'bg-[#15181c] text-slate-100 md:border-[#444e5d]'
                : 'bg-white text-gray-900 md:border-gray-100'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* MODAL HEADER */}
            <div className={`mb-2 flex items-center justify-between md:sticky md:top-0 md:z-10 md:mb-0 md:border-b md:px-6 md:py-5 md:backdrop-blur-md ${
              isDarkTheme
                ? 'md:border-[#444e5d]/60 md:bg-[#15181c]/95 text-slate-100'
                : 'md:border-gray-100 md:bg-white/95 text-gray-900'
            }`}>
              <div>
                <h2 className="text-base font-bold md:text-xl">{T.title}</h2>
                {facets.specs.length > 0 && (
                  <p className={`mt-1 hidden text-sm md:block ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>{T.legend}</p>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className={`rounded-full p-1.5 transition ${
                  isDarkTheme ? 'text-slate-400 hover:bg-[#1f242d] hover:text-slate-100' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                }`}
                aria-label={T.apply}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {facets.specs.length > 0 && (
              <p className={`mb-3 text-xs md:hidden ${isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>{T.legend}</p>
            )}

            <div className="md:grid md:grid-cols-[minmax(0,1fr)_260px] md:gap-5 md:p-6">
              <div>
                {/* ACTIVE SELECTED FILTERS SUMMARY */}
                {selected.length > 0 && (
                  <div className={`mb-4 rounded-2xl border p-3 md:p-4 ${
                    isDarkTheme
                      ? 'border-[#c9a24b]/40 bg-[#c9a24b]/10 text-slate-100'
                      : 'border-brand-100 bg-brand-50/40 text-gray-800'
                  }`}>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wider">{T.selected}</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.map(({ specName, option, kind }) => (
                        <button
                          key={`${specName}:${option}`}
                          onClick={() => onRemove(specName, option)}
                          className={`flex items-center gap-1 rounded-2xl border px-3 py-1.5 text-xs font-medium ${chipClasses[kind]}`}
                        >
                          {getLocalizedSpecName(specName)}: {option}
                          <X className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ATTRIBUTE SPECS */}
                {facets.specs.map((spec) => {
                  const specKey = spec.code || spec.name_uk
                  const specDisplayName = localizedName(lang, spec.name_uk, spec.name_en)
                  const query = (specSearch[specKey] ?? '').trim().toLowerCase()
                  const matchingOptions = query
                    ? spec.options.filter((option) => option.toLowerCase().includes(query))
                    : spec.options
                  const isExpanded = expanded[specKey] ?? false
                  const canCollapse = !query && matchingOptions.length > COLLAPSE_LIMIT
                  const visibleOptions =
                    canCollapse && !isExpanded ? matchingOptions.slice(0, COLLAPSE_LIMIT) : matchingOptions

                  return (
                    <div
                      key={specKey}
                      className={`mb-4 rounded-2xl border p-3.5 md:p-4 ${
                        isDarkTheme
                          ? 'border-[#444e5d]/60 bg-[#1f242d]'
                          : 'border-gray-100 bg-gray-50/70'
                      }`}
                    >
                      <p className={`mb-2 text-sm font-bold md:text-base ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                        {specDisplayName}
                      </p>

                      {spec.options.length > SEARCHABLE_THRESHOLD && (
                        <div className="relative mb-2.5">
                          <Search className={`absolute left-2.5 top-2.5 h-3.5 w-3.5 md:left-3 md:top-3 ${
                            isDarkTheme ? 'text-slate-400' : 'text-gray-400'
                          }`} />
                          <input
                            type="text"
                            value={specSearch[specKey] ?? ''}
                            onChange={(e) =>
                              setSpecSearch((prev) => ({ ...prev, [specKey]: e.target.value }))
                            }
                            placeholder={T.searchPlaceholder}
                            className={`w-full rounded-xl border py-1.5 pl-8 pr-3 text-xs focus:outline-none md:py-2 md:pl-9 md:text-sm font-medium transition ${
                              isDarkTheme
                                ? 'border-[#444e5d] bg-[#28303c] text-slate-100 placeholder-slate-400 focus:border-[#c9a24b]'
                                : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500'
                            }`}
                          />
                        </div>
                      )}

                      {matchingOptions.length === 0 ? (
                        <p className={`text-xs ${isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>{T.noMatches}</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {visibleOptions.map((option) => {
                            const state = chipState(include, exclude, specKey, option)
                            return (
                              <button
                                key={option}
                                onClick={() => onCycle(specKey, option)}
                                className={`rounded-2xl border px-3 py-1.5 text-xs font-medium transition-colors md:px-3.5 md:py-2 ${chipClasses[state]}`}
                              >
                                {option}
                              </button>
                            )
                          })}
                          {canCollapse && (
                            <button
                              onClick={() =>
                                setExpanded((prev) => ({ ...prev, [specKey]: !isExpanded }))
                              }
                              className={`rounded-2xl border border-dashed px-3 py-1.5 text-xs font-medium transition ${
                                isDarkTheme
                                  ? 'border-[#444e5d] text-slate-400 hover:text-slate-200'
                                  : 'border-gray-300 text-gray-500 hover:text-gray-800'
                              }`}
                            >
                              {isExpanded
                                ? T.showLess
                                : `${T.showMore} (+${matchingOptions.length - COLLAPSE_LIMIT})`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* ASIDE / SIDEBAR: PRICE & VARIANT FILTERS */}
              <aside className={`mt-4 rounded-2xl border p-4 md:mt-0 md:sticky md:top-[96px] md:self-start ${
                isDarkTheme
                  ? 'border-[#444e5d]/60 bg-[#1f242d]'
                  : 'border-gray-100 bg-gray-50/80'
              }`}>
                {/* PRICE RANGE */}
                <div className="mb-4">
                  <p className={`mb-2 text-sm font-bold md:text-base ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{priceRangeLabel}</p>
                  <div className="flex gap-2 md:grid md:grid-cols-1">
                    <div className="flex-1">
                      <label className={`mb-1 block text-xs font-medium ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>{T.priceMin}</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={priceMin}
                        onChange={(e) => onPriceMinChange(e.target.value)}
                        placeholder={String(facets.price_min)}
                        className={`w-full rounded-xl border px-3 py-2 text-sm font-medium focus:outline-none transition ${
                          isDarkTheme
                            ? 'border-[#444e5d] bg-[#28303c] text-slate-100 placeholder-slate-400 focus:border-[#c9a24b]'
                            : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500'
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`mb-1 block text-xs font-medium ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>{T.priceMax}</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={priceMax}
                        onChange={(e) => onPriceMaxChange(e.target.value)}
                        placeholder={String(facets.price_max)}
                        className={`w-full rounded-xl border px-3 py-2 text-sm font-medium focus:outline-none transition ${
                          isDarkTheme
                            ? 'border-[#444e5d] bg-[#28303c] text-slate-100 placeholder-slate-400 focus:border-[#c9a24b]'
                            : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* VARIANT DROPDOWN (GROUPED BY OPTION TYPE) */}
                {facets.variant_names.length > 0 && (
                  <div className="mb-4">
                    <p className={`mb-2 text-sm font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{T.variant}</p>
                    <select
                      value={variantName}
                      onChange={(e) => onVariantNameChange(e.target.value)}
                      className={`w-full rounded-xl border px-3 py-2 text-sm font-medium focus:outline-none transition ${
                        isDarkTheme
                          ? 'border-[#444e5d] bg-[#28303c] text-slate-100 focus:border-[#c9a24b]'
                          : 'border-gray-200 bg-white text-gray-900 focus:border-brand-500'
                      }`}
                    >
                      <option value="" className={isDarkTheme ? 'bg-[#28303c] text-slate-100' : 'bg-white text-gray-900'}>
                        {T.variantAny}
                      </option>
                      {(facets.variant_groups && facets.variant_groups.length > 0
                        ? facets.variant_groups
                        : [
                            {
                              name_uk: "Об'єм",
                              name_en: "Volume",
                              variants: facets.variant_names.filter((n) => {
                                const l = n.toLowerCase()
                                return l.includes('мл') || l.includes('ml') || l.includes('флакон')
                              }),
                            },
                            {
                              name_uk: "Колір / Варіант",
                              name_en: "Color / Variant",
                              variants: facets.variant_names.filter((n) => {
                                const l = n.toLowerCase()
                                return !l.includes('мл') && !l.includes('ml') && !l.includes('флакон')
                              }),
                            },
                          ].filter((g) => g.variants.length > 0)
                      ).map((group) => (
                        <optgroup
                          key={group.name_uk}
                          label={localizedName(lang, group.name_uk, group.name_en)}
                          className={isDarkTheme ? 'bg-[#1f242d] text-[#c9a24b] font-bold' : 'bg-gray-100 text-gray-800 font-bold'}
                        >
                          {group.variants.map((name) => (
                            <option key={name} value={name} className={isDarkTheme ? 'bg-[#28303c] text-slate-100' : 'bg-white text-gray-900'}>
                              {name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* FOOTER BUTTONS */}
                <div className="mt-4 flex gap-2 md:grid md:grid-cols-1">
                  <button
                    onClick={onClear}
                    className={`flex-1 rounded-xl border py-2.5 text-xs font-semibold transition ${
                      isDarkTheme
                        ? 'border-[#444e5d] bg-[#28303c] text-slate-200 hover:border-slate-500'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    {T.clear}
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className={`flex-1 rounded-xl py-2.5 text-xs font-bold shadow-md transition ${
                      isDarkTheme
                        ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d4af37]'
                        : 'bg-brand-600 text-white hover:bg-brand-700'
                    }`}
                  >
                    {T.apply}
                  </button>
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
